import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import TranslationPromoteAction from "@/v2-backoffice-client/translation/missing/TranslationPromoteAction"
import { useTranslationMissingQuery } from "@/v2-backoffice-client/translation/missing/translation.missing.query"

// A UI string that exists only in the code fallback catalog, not in the DB.
type TranslationMissingRow = {
  code: string
  name: string
  description: string
}

const MISSING_COLUMNS = createColumns<TranslationMissingRow>({
  enableSelection: true,
  enableSorting: true,
  columns: {
    code: { id: "code", label: "Code" },
    name: { id: "name", label: "English (fallback)" },
    description: { id: "description", label: "Description" },
  },
})

export type TranslationMissingTableProps = Omit<
  TableProps<TranslationMissingRow>,
  "data" | "columns"
>

// "Missing in database": texts that ship in the code (*.text.ts) but have no
// row in the translation database yet. Select rows to promote them into the
// database (optionally with AI-drafted translations). Contextual help lives
// in the sidebar item's info tooltip. The mirror image is
// TranslationOrphanedTable.
function TranslationMissingTable(props: TranslationMissingTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error } = useTranslationMissingQuery()

  errorToast(error, { title: "Failed to load texts missing in database" })

  const rows: TranslationMissingRow[] = (data?.getMissingInCodeKeys ?? []).map(
    (key) => ({
      code: key.Code,
      name: key.Name ?? "",
      description: key.Description ?? "",
    }),
  )

  return (
    <Table
      className={cn("translation-missing-table", className)}
      tableId="translation-missing-table"
      data={rows}
      columns={MISSING_COLUMNS}
      getRowId={(row) => row.code}
      isCellsWrapped
      enableInfiniteScroll
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.Search placeholder="Search code or text..." />
        <div className="flex-1" />
        <Table.ToolbarMenu exportFileName="missing-in-database" />
      </Table.Toolbar>

      <Table.Content isLoading={loading} error={error} />

      <Table.SelectionToolbar<TranslationMissingRow>>
        {(selected, reset) => (
          <TranslationPromoteAction
            selectedRows={selected}
            onComplete={reset}
          />
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TranslationMissingTable
